'use strict';

export default angular
  .module('maestro.services.data.reports', [
    'maestro.services.webcore'
  ])
  .service('ReportsService', ReportsService)
  .name;

function ReportsService(webcore, $q, CacheService) {

  /**
   * Get report groups.
   * @param {number} organizationId
   * @returns {Promise}
   */
  this.getReportGroups = CacheService.cachePromiseFunc((organizationId) => {
    return $q.when(
      webcore.services.reports.getReportGroups(organizationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Set organization status for report group.
   * This API put organization into report group, or change its status.
   *
   * @param {number} organizationId Organization ID.
   * @param {number} reportGroupId Report group ID.
   * @param {boolean} activate Activate or deactivate report group.
   * @param [number] notificationGroupId Organization notification group.
   * @returns {Promise}
   */
  this.setReportGroupStatus = (organizationId, reportGroupId, activate, notificationGroupId) => {
    this.getReportGroups.clearEntireCache();
    this.getAvailableReports.clearEntireCache();

    if (activate) {
      return $q.when(
        webcore.services.reports
          .setReportGroup(organizationId, reportGroupId, notificationGroupId)
          .then(data => $q.resolve(data), err => $q.reject(err))
      );
    } else {
      return $q.when(
        webcore.services.reports
          .deleteReportGroup(organizationId, reportGroupId)
          .then(data => $q.resolve(data), err => $q.reject(err))
      );
    }
  };

  /**
   * Get available reports.
   * This API provide information about available reports.
   *
   * @param [params] Requested parameters.
   * @param {number} [params.reportId] Optional filter by report ID.
   * @param {number} [params.organizationId] Optional filter by organization ID.
   * @param {boolean} [params.analytic] Optional filter by 'analytic' report flag.
   * @returns {Promise}
   */
  this.getAvailableReports = CacheService.cachePromiseFunc((params) => {
    return $q.when(
      webcore.services.reports.getAvailableReports(params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Generate specific report.
   * The result can provided in CSV or HTML format. Long generated reports can be delivered by email.
   * All report parameters should be provided in the query string.
   *
   * @param params Requested parameters.
   * @param {number} params.reportId Report ID.
   * @param {number} params.deliveryType Way to return report data:
   *  2 - by email
   *  3 - prepare for download
   * @param {number} [params.organizationId] Generate report for specific organization.
   * @returns {Promise<ArrayBuffer>}
   */
  this.generateReport = (params) => {
    return $q.when(
      webcore.services.reports.generateReport(params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get report execution data.
   * The API returns the saved data of the report executions. Reports are executed according to the schedules defined for the report groups to which they belong.
   *
   * @param params Requested parameters.
   * @param {number} params.reportId Report ID.
   * @param {number} params.reportGroupId Report group ID.
   * @param {string|number} params.startDate
   * @param {string|number} params.endDate
   * @param {number} [params.organizationId] Organization ID, required for organizational report group.
   * @param {boolean} [params.subOrgs] Select report executions for sub-organizations.
   * @returns {Promise}
   */
  this.getReportExecutions = CacheService.cachePromiseFunc((params) => {
    return $q.when(
      webcore.services.reports.getReportExecutions(params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  }, 60 * 60 * 1000);

  /**
   * Get report data by the token generated during report generation.
   * Used to poll for asynchronous report completion. Resolves once the report is ready.
   *
   * @param {string} token JWT token returned by generateReport.
   * @param {number} [responseFormat] Report data response format (0 = ZIP, 1 = JSON).
   * @returns {Promise}
   */
  this.getReportData = (token, responseFormat) => {
    return $q.when(
      webcore.services.reports.getReportData(token, responseFormat)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get System status.
   * This API return detailed statistics about different system componets for the last 24 hours.
   *
   * @param {number} [organizationId] Organization ID.
   * @returns {Promise}
   */
  this.getSystemStatus = (organizationId) => {
    return $q.when(
      webcore.services.reports
        .getSystemStatus(organizationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

}
